/**
 * Frontend mirror of /app/backend/opening_hours.py — answers "is this venue
 * open today?" using `open_days` (weekday array) and `season_open_from/to`
 * (months 1-12, inclusive, wraps year boundary).
 *
 * Also exports `isEveningMode()` and `effectiveDate()` — frontend mirrors of
 * /app/backend/time_helpers.py — so visitor-facing surfaces (the homepage
 * "pick" tile, etc.) can flip from today → tomorrow at 18:00 UK time in
 * exactly the same way the backend does.
 *
 * Conservative: unknown/missing data → assume open, so we don't drop venues
 * the admin hasn't fully annotated yet.
 */
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// After 18:00 UK time the site talks about tomorrow rather than today.
const EVENING_FLIP_HOUR = 18;

function ukHour(now = new Date()) {
  // Intl gives us the UK-local hour regardless of the visitor's timezone, so
  // a US visitor browsing at midnight ET (5am UK) still sees today, and a
  // UK visitor at 7pm sees tomorrow. Cheap, no library needed.
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(now);
  return parseInt(parts.find((p) => p.type === 'hour').value, 10);
}

export function isEveningMode(now = new Date()) {
  return ukHour(now) >= EVENING_FLIP_HOUR;
}

export function effectiveDate(now = new Date()) {
  const d = new Date(now);
  if (isEveningMode(now)) d.setDate(d.getDate() + 1);
  return d;
}

export function effectiveDayLabel(now = new Date()) {
  return isEveningMode(now) ? 'tomorrow' : 'today';
}

function inSeason(month, start, end) {
  if (start == null || end == null) return true;
  if (start <= end) return month >= start && month <= end;
  // wrap-around (e.g. Nov–Mar Christmas lights)
  return month >= start || month <= end;
}

export function isOpenToday(attraction, now = new Date()) {
  if (!attraction) return true;
  const month = now.getMonth() + 1; // JS months are 0-11
  if (!inSeason(month, attraction.season_open_from, attraction.season_open_to)) {
    return false;
  }
  const days = (attraction.open_days || []).filter((d) => WEEKDAY_KEYS.includes(d));
  if (days.length === 0) return true; // unknown → assume open
  const todayKey = WEEKDAY_KEYS[(now.getDay() + 6) % 7]; // JS Sunday=0 → shift so Monday=0
  return days.includes(todayKey);
}
